/**
 * Plugin de inicialización de infraestructura para Web
 *
 * Registra todos los adapters con los puertos de @pepi/core
 * al iniciar la aplicación Vue.
 */

import type { App } from 'vue'
import type { Router } from 'vue-router'
import {
  registerStorage,
  registerSecureStorage,
  registerNetwork,
  registerNavigation,
  registerNotification,
  registerVoiceInput,
  registerVoiceOutput
} from '@pepi/core'
import {
  createWebStorageAdapters,
  createWebNetworkAdapter,
  createWebNavigationAdapter,
  createWebNotificationAdapter,
  createWebVoiceInputAdapter,
  createWebVoiceOutputAdapter
} from '@/adapters'

export interface BootstrapOptions {
  router: Router
}

/**
 * Inicializa la infraestructura web registrando adapters en los puertos
 */
export function initializeInfrastructure(options: BootstrapOptions): void {
  // Storage
  const { storage, secureStorage } = createWebStorageAdapters()
  registerStorage(storage)
  registerSecureStorage(secureStorage)

  // Network
  const network = createWebNetworkAdapter()
  registerNetwork(network)

  // Notification.
  // `WebNotificationAdapter` existía desde el principio y NO se registraba:
  // web enchufaba storage, network, navigation y voz, pero se dejaba este.
  // Mientras nada de `packages/core` avisara al usuario no se notaba; ahora
  // `createChatSession` avisa por el puerto, así que sin esto el "Historial
  // limpiado" se perdería solo en web.
  const notification = createWebNotificationAdapter()
  registerNotification(notification)

  // Navigation
  const navigation = createWebNavigationAdapter(options.router)
  registerNavigation(navigation)

  // Voice
  registerVoiceInput(createWebVoiceInputAdapter())
  registerVoiceOutput(createWebVoiceOutputAdapter())
}

/**
 * Plugin de Vue para inicializar infraestructura
 */
export const infrastructurePlugin = {
  install(app: App, options: BootstrapOptions): void {
    initializeInfrastructure(options)

    // Opcional: hacer adapters disponibles globalmente
    app.config.globalProperties.$infrastructure = {
      initialized: true
    }
  }
}

export default infrastructurePlugin
